import { useContext, useState } from "react";
import OtpInput from "react-otp-input";
import Button from "../../../components/Button";
import { ErrorIcon } from "../../../components/SVGs";
import { verifyOTP, resendPin } from "../../../services/User";
import { useNavigate } from "react-router";
import { MasterDataContext } from "../../../contexts/MasterDataContext";
import { useTranslation } from "react-i18next";
import useToast from "../../../hooks/useToast";
import PropTypes from "prop-types";
import { FireBaseContext } from "../../../contexts/FireBase";
import { Typography } from "@mui/material";

const customStyles = {
  heading: {
    userSelect: "none",
    "& p": {
      color: "rgba(81, 81, 81, 1)",
      fontSize: "1rem",
      textAlign: "center",
    },
  },
  otp: {
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
  },
  error: {
    "& input": { color: "rgb(245 79 100)" },
  },
  // otpContainer: {
  //   justifyContent: "center",
  //   direction: "ltr",
  //   "&> div> input": {
  //     border: "1px solid rgba(217, 216, 216, 1)",
  //     height: "3rem",
  //     width: "4rem !important",
  //     fontSize: "1.5rem",
  //     borderRadius: "0.25rem",
  //     "@media only screen and (max-width: 414px)": {
  //       height: "2.5rem",
  //       width: "3.5rem !important",
  //       fontSize: "1rem",
  //     },
  //   },
  //   "& > div": {
  //     margin: "0 0.75rem",
  //   },
  //   "& > div > input:hover": {
  //     borderColor: "rgb(0 0 0 /87%)",
  //   },
  //   "& > div > input:focus-within": {
  //     outlineColor: "rgb(98 195 238)",
  //   },
  //   "& > div > input:focus": {
  //     border: "2px solid rgb(98 195 238)",
  //   },
  // },
  resendLink: {
    textAlign: "center",
    marginTop: "20px",
    "& span": {
      cursor: "pointer",
      fontSize: "1rem",
      color: "rgb(81 81 81)",
      textDecoration: "underline",
      "&:hover": {
        textShadow: "-2px -2px 3px rgb(227 239 248)",
        color: "rgb(98 195 238)",
      },
    },
  },
  backToLogin: {
    width: "100%",
    display: "flex",
    paddingTop: "1rem",
    justifyContent: "flex-end",
  },
  otpContainer: {
    display: "flex",
    justifyContent: "center",
    flexDirection: "row",
    direction: "ltr",
    gap: "24px",
  },
  inputStyle: {
    border: "1px solid rgba(217, 216, 216, 1)",
    height: "3rem",
    width: "4rem",
    fontSize: "1.5rem",
    borderRadius: "0.25rem",
    textAlign: "center",
    "&:focus": {
      border: "2px solid rgb(98 195 238)",
      outline: "none",
    },
  },
  errorStyle: {
    color: "rgb(245 79 100)",
    border: "1px solid rgb(245 79 100)",
  },
};

function OtpVerification({ userInfo, setActiveStep }) {
  const [otp, setOtp] = useState("");
  const [buttonDisable, setButtonDisable] = useState(true);
  const [error, setError] = useState(false);
  const { dispatch } = useContext(MasterDataContext);
  const toast = useToast();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { logEvent } = useContext(FireBaseContext);

  const onOtpChange = (otp) => {
    setButtonDisable(true);
    setError(false);
    if (otp.length === 4) setButtonDisable(false);
    setOtp(otp);
  };

  const verifyOtp = async () => {
    setButtonDisable(true);
    const data = {
      _contactType: userInfo._contactType,
      _emailOrMobile:
        userInfo._contactType === "email"
          ? userInfo._email
          : userInfo._mobileNumber,
      _otpType: "LOGIN",
      _pin: otp,
      _userId: userInfo._id,
    };
    try {
      const response = await verifyOTP(data);
      if (response && response._statusCode === 200) {
        const {
          _token,
          _refreshToken,
          _name: firstName,
          _lastName: lastName,
          _schoolId,
          _schoolName,
          _email,
          _id,
        } = response._entity;
        logEvent("ClickOnVerifyOTP", {
          Status: "Success",
          SchoolID: _schoolId,
          UserID: _id,
        });
        dispatch({
          type: "userDetails",
          payload: { firstName, userId: _id, lastName, email: _email },
        });
        dispatch({
          type: "schoolDetails",
          payload: { schoolId: _schoolId, schoolName: _schoolName },
        });
        localStorage.setItem("abjadToken", _token);
        localStorage.setItem("refreshToken", _refreshToken);
        navigate("/teachers-reports");
      } else {
        logEvent("ClickOnVerifyOTP", {
          Status: "Failure",
          UserID: userInfo._id,
        });
        setError(true);
      }
    } catch (error) {
      console.log(error);
    }
    setButtonDisable(false);
  };

  const resendHandler = async () => {
    let payload = {};
    if (userInfo._contactType === "email") payload["_email"] = userInfo._email;
    else payload["_mobileNumber"] = userInfo._mobileNumber;
    try {
      const response = await resendPin(payload);
      if (response) {
        if (response._statusCode === 200) {
          logEvent("ClickOnResendOTP", {
            UserID: userInfo._id,
          });
          toast({
            label: t("Success Label"),
            message: t("ResendMessage"),
            rtl: i18n.dir() === "rtl",
            type: "success",
          });
        }
      } else throw response;
    } catch (error) {
      console.log({ error });
    }
  };

  return (
    <>
      <Typography component="div" sx={customStyles.heading}>
        <p style={{ color: "rgba(98, 195, 238, 1)" }}>
          {userInfo.mobileNumber ? userInfo._mobileNumber : userInfo._email}
        </p>
        <p>{t("OTP Heading")}</p>
      </Typography>
      <Typography component="div" sx={customStyles.otp}>
        <OtpInput
          value={otp}
          onChange={onOtpChange}
          numInputs={4}
          isInputNum
          shouldAutoFocus
          containerStyle={customStyles.otpContainer}
          inputStyle={{
            ...customStyles.inputStyle,
            ...(error ? customStyles.errorStyle : {}),
          }}
          renderInput={(props) => <input {...props} />}
        />
        {error ? (
          <p style={{ display: "flex", justifyContent: "center" }}>
            <ErrorIcon style={{ fontSize: "1.25rem" }} />
            <span style={{ fontSize: "0.75rem", color: "rgb(245 79 100)" }}>
              {t("Wrong OTP")}
            </span>
          </p>
        ) : null}
      </Typography>
      <div style={{ marginTop: "30px" }}>
        <Button
          label={t("Verification")}
          styles={{ width: "300px" }}
          action={verifyOtp}
          disable={buttonDisable}
        />
        <Typography component="div" sx={customStyles.resendLink}>
          <span onClick={resendHandler}>{t("Resend Code")}</span>
        </Typography>
      </div>
      <Typography component="div" sx={customStyles.backToLogin}>
        <Button
          label={t("Back To Login")}
          white
          styles={{ width: "25%" }}
          action={() => setActiveStep(1)}
        />
      </Typography>
    </>
  );
}

OtpVerification.propTypes = {
  userInfo: PropTypes.object.isRequired,
  setActiveStep: PropTypes.func,
};

export default OtpVerification;
